/* ============================================================
   LEGEND — Entry marks. Ported from the root legend/entrymarks.jsx.
   Three small marks so the trader sees, at the moment of entry,
   whether price sits where HIS entries have worked — for the armed
   side (the header's Long / Short):
     1 · range bars     5m + 15m low–high beside the CEIL/FLOOR rail,
                        in thirds, shaded by his record for the side
     2 · turning point  MV-5's active setup for the armed side, dashed;
                        more than chaseAtr (1 ATR, the 1-minute ATR14) past
                        it is hatched red — the last few candles up to the
                        axis only (the prototype's 2 %: moved to ATR 10-22)
     3 · tag colour     the last-price tag on the axis turns green /
                        amber / red; hover or tap → one line of why
   Input: the EntryMarks shape (lib/entry-marks.ts — the rules, and how
   each mark is measured the way the journal measured his trades). The
   15m bar is context: shaded the same way, it never moves the tag (yet).
   The day picture (10-23): 4H (the last 4 hours) and D (the day since
   04:00 ET) bars left of the 15m one, price-aligned like the others —
   clipped at the pane with an arrow where the day's high / low is off
   screen — context only, shaded from his record (4H grey until he has
   one). Labels in one row at the foot of the column.
   Repaints only when something it draws moved (the dirty key — the
   TrendLineLayer pattern), and not at all in a hidden tab.
   ============================================================ */
import React from 'react';
import { EM_THIRD, emUsd, emPx, emAtr, readMarks, type EntryMarks, type EntryRecord } from '../lib/entry-marks';

const { useRef: useRefEM, useEffect: useEffectEM, useState: useStateEM } = React;

export const EM_W = 44;   // the range column, between the S/R rail and the price axis: D · 4H · 15m · 5m
const EM_AXIS = 64;       // = --axis-w (ChartPane pins the price scale's minimumWidth to it)
const EM_LAST = 8;        // the turning point + hatch span the last 8 candles
const EM_TP_FONT = '600 9.5px "IBM Plex Sans", sans-serif';
const EM_MONO = '600 8px "IBM Plex Mono", monospace';
const EM_LAB = '600 7px "IBM Plex Mono", monospace';
const EM_FOOT = 12;       // the labels row at the foot of the column
// the four bars, left → right: x offset in the column, width, fill alpha (the context bars fainter)
const EM_BARS = [
  { tf: 'D', x: 2, w: 6, a: 0.42, lab: 'D' },
  { tf: '4h', x: 13, w: 5, a: 0.42, lab: '4H' },
  { tf: '15m', x: 23, w: 6, a: 0.42, lab: '15' },
  { tf: '5m', x: 33, w: 8, a: 0.9, lab: '5' },
] as const;
/** which bar an x inside the column is over (the gaps go to the nearer bar) */
const emBarAtX = (x: number): string => (x < 10.5 ? 'D' : x < 20.5 ? '4h' : x < 31 ? '15m' : '5m');

function emRgba(hex: string, a: number) {
  const h = (hex || '#8b93a1').replace('#', '');
  return 'rgba(' + parseInt(h.slice(0, 2), 16) + ',' + parseInt(h.slice(2, 4), 16) + ',' + parseInt(h.slice(4, 6), 16) + ',' + a + ')';
}

function emRR(ctx: any, x: number, y: number, w: number, h: number, r: number) {
  r = Math.min(r, w / 2, h / 2); ctx.beginPath();
  if (ctx.roundRect) ctx.roundRect(x, y, w, h, r);
  else { ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r); ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath(); }
}
function emChip(ctx: any, x: number, y: number, text: string, o: any) {
  ctx.font = o.font;
  const cw = ctx.measureText(text).width + 12, ch = o.h || 16, cx = o.right ? x - cw : x, cy = Math.round(y - ch / 2);
  ctx.fillStyle = o.bg; emRR(ctx, cx, cy, cw, ch, 4); ctx.fill();
  if (o.bd) { ctx.strokeStyle = o.bd; ctx.lineWidth = 1; emRR(ctx, cx + 0.5, cy + 0.5, cw - 1, ch - 1, 4); ctx.stroke(); }
  ctx.fillStyle = o.fg; ctx.textAlign = 'left'; ctx.textBaseline = 'middle'; ctx.fillText(text, cx + 6, cy + ch / 2 + 0.5);
}

/** the chart's candles when they are the symbol's this layer was mounted for (ChartPane getCandles contract) */
function emCandles(api: any, sym: string): any[] {
  const src = api && api.getCandles ? api.getCandles() : null;
  return src && src.symbol === sym && Array.isArray(src.candles) ? src.candles : [];
}

// ---- what a paint depends on: an unchanged key skips the frame ----
function emKey(cv: any, api: any, s: any): string {
  const host = cv.parentElement, m = s.marks, R = m && m.ranges, r5 = R && R['5m'];
  let key = host.clientWidth + 'x' + host.clientHeight + ':' + (window.devicePixelRatio || 1) + '|' + s.rw + '|' + s.price;
  if (!r5 || s.price == null) return key + '|none';
  const r15 = R['15m'], r4 = R['4h'], rD = R.D, pv = m.pivot, cs = emCandles(api, s.symbol), n = cs.length;
  let range: any = null, paneH = 0;
  try { range = api.chart.timeScale().getVisibleLogicalRange(); } catch (e) { range = null; }
  try { paneH = api.chart.panes()[0].getHeight(); } catch (e) { paneH = 0; }
  const y1 = api.priceToY(r5.hi), y2 = api.priceToY(r5.lo);
  return key + '|' + m.side + ':' + r5.lo + ':' + r5.hi
    + '|' + (r15 ? r15.lo + ':' + r15.hi + ':' + (r15.closed ? r15.closed.lo + ':' + r15.closed.hi : '') : '')
    + '|' + (r4 ? r4.lo + ':' + r4.hi : '') + '|' + (rD ? rD.lo + ':' + rD.hi : '')
    + '|' + (((m.zonesDay || {})[m.side] || []).join(',')) + '|' + (((m.zones4h || {})[m.side] || []).join(','))
    + '|' + ((m.zones || {})[m.side] || []).join(',') + '|' + (pv ? pv.price + ':' + pv.tf + ':' + pv.setup + ':' + (pv.stale ? pv.asOf : '') : '') + ':' + m.chaseAtr + ':' + (m.atr == null ? 'n' : m.atr.toPrecision(4))
    + '|' + paneH + '|' + (range ? range.from.toFixed(2) + ':' + range.to.toFixed(2) : 'n') + '|' + (y1 == null ? 'n' : y1.toFixed(1)) + ':' + (y2 == null ? 'n' : y2.toFixed(1))
    + '|' + n + ':' + (n ? cs[n - 1].time : 0) + '|' + (s.tip ? s.tip.key + ':' + (s.tip.price || '') : '');
}

// ---- one frame: hatch + turning point → range column → labels; then glue the hit areas ----
function emDraw(cv: any, api: any, s: any, hb: any, ht: any, tp: any) {
  if (!cv || !api || !api.chart) return;
  const host = cv.parentElement, w = host.clientWidth, h = host.clientHeight, dpr = window.devicePixelRatio || 1;
  if (cv.width !== Math.round(w * dpr) || cv.height !== Math.round(h * dpr)) { cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr); cv.style.width = w + 'px'; cv.style.height = h + 'px'; }
  const ctx = cv.getContext('2d'); ctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctx.clearRect(0, 0, w, h);
  const m = s.marks, R = m && m.ranges, r5 = R && R['5m'], r15 = R && R['15m'];
  if (!r5 || s.price == null) { if (hb) hb.style.display = 'none'; if (ht) ht.style.display = 'none'; return; }
  const c = s.colors, Y = (p: number) => api.priceToY(p);
  let paneH = h; try { const p0 = api.chart.panes()[0]; paneH = (p0.getHeight && p0.getHeight()) || p0.getHTMLElement().clientHeight || h; } catch (e) {}
  const colR = w - EM_AXIS, colL = colR - EM_W, free = colL - s.rw;
  const zs = (m.zones || {})[m.side] || [];
  const zc = (z: string) => (z === 'good' ? c.up : z === 'bad' ? c.down : z === 'none' ? '#8b93a1' : c.warn);
  const zonesOfBar = (tf: string): string[] => (tf === 'D' ? ((m.zonesDay || {})[m.side] || ['none', 'none', 'none'])
    : tf === '4h' ? ((m.zones4h || {})[m.side] || ['none', 'none', 'none']) : zs);
  const pv = m.pivot, A = pv && pv.stale ? 0.42 : 1;
  let chip: any = null, cchip: any = null;

  // 2 · turning point + chase hatch, over the last few candles only
  if (pv) {
    const ts = api.chart.timeScale(), X = (t: number) => { try { return ts.timeToCoordinate(t); } catch (e) { return null; } };
    const cs = emCandles(api, s.symbol), n = cs.length;
    const xa = n > 1 ? X(cs[n - 1].time) : null, xb = n > 1 ? X(cs[n - 2].time) : null;
    const sp = xa != null && xb != null ? Math.max(2, xa - xb) : 7;
    let x0 = n ? X(cs[Math.max(0, n - EM_LAST)].time) : null;
    x0 = x0 == null ? free - EM_LAST * sp : x0 - sp / 2;
    x0 = Math.max(4, Math.min(free - 24, x0));
    const chase = m.chaseAtr == null ? 1 : m.chaseAtr, atr = m.atr != null && m.atr > 0 ? m.atr : null;
    // the chase line: chaseAtr ATR past the turning point (no ATR yet → no chase line, nothing hatched)
    const yP = Y(pv.price), yC = atr ? Y(pv.price + (m.side === 'short' ? -1 : 1) * chase * atr) : null;
    ctx.save(); ctx.beginPath(); ctx.rect(0, 0, colR, paneH); ctx.clip(); ctx.globalAlpha = A;
    if (yC != null) {
      const top = m.side === 'short' ? Math.max(0, yC) : 0, bot = m.side === 'short' ? paneH : Math.min(paneH, yC);
      if (bot - top > 1) {
        ctx.save(); ctx.beginPath(); ctx.rect(x0, top, free - x0, bot - top); ctx.clip();
        ctx.fillStyle = emRgba(c.down, 0.08); ctx.fillRect(x0, top, free - x0, bot - top);
        ctx.strokeStyle = emRgba(c.down, 0.45); ctx.lineWidth = 1; ctx.beginPath();
        const H = bot - top; for (let k = -H; k < free - x0 + 6; k += 6) { ctx.moveTo(x0 + k, bot); ctx.lineTo(x0 + k + H, top); }
        ctx.stroke(); ctx.restore();
        if (yC >= 0 && yC <= paneH) {
          const yy = Math.round(yC) + 0.5;
          ctx.strokeStyle = emRgba(c.down, 0.85); ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(x0, yy); ctx.lineTo(free, yy); ctx.stroke();
          cchip = { x: x0 + 3, y: m.side === 'short' ? yC + 10 : yC - 10, text: emAtr(chase) + ' ATR past' };
        }
      }
    }
    if (yP != null && yP >= -2 && yP <= paneH + 2) {
      const yy = Math.round(yP) + 0.5;
      ctx.strokeStyle = pv.stale ? 'rgba(163,169,179,0.95)' : 'rgba(238,240,243,0.9)'; ctx.lineWidth = 1.25; ctx.setLineDash([5, 4]);
      ctx.beginPath(); ctx.moveTo(x0, yy); ctx.lineTo(colR, yy); ctx.stroke(); ctx.setLineDash([]);
      chip = { x0, y: yP, text: 'turning point ' + emPx(pv.price) + ' · ' + pv.tf + ' ' + pv.setup + (pv.stale ? ' · last read ' + (pv.asOf || '—') : '') };
    }
    ctx.restore();
  }

  // 1 · range column: D + 4H + 15m (context, faint) + 5m (drives the tag)
  ctx.globalAlpha = 1;
  ctx.fillStyle = 'rgba(7,8,10,0.6)'; ctx.fillRect(colL, 0, EM_W, paneH);
  if (!s.rw) { ctx.fillStyle = 'rgba(255,255,255,0.07)'; ctx.fillRect(colL, 0, 1, paneH); }
  ctx.save(); ctx.beginPath(); ctx.rect(colL, 0, EM_W, paneH - EM_FOOT); ctx.clip();
  const bar = (r: any, x: number, bw: number, a: number, zones: string[]) => {
    let yH = Y(r.hi), yL = Y(r.lo); if (yH == null || yL == null) return null;
    if (yL - yH < 6) { const mid = (yH + yL) / 2; yH = mid - 3; yL = mid + 3; }
    const cl = r.closed, yCH = cl && cl.hi < r.hi ? Y(cl.hi) : yH, yCL = cl && cl.lo > r.lo ? Y(cl.lo) : yL;
    for (let k = 0; k < 3; k++) {
      const t = yH + (yL - yH) * k / 3 + (k ? 0.5 : 0), b = yH + (yL - yH) * (k + 1) / 3 - (k < 2 ? 0.5 : 0), fill = zc(zones[k]!);
      const sT = Math.max(t, yCH), sB = Math.min(b, yCL);
      if (sB > sT) { ctx.fillStyle = emRgba(fill, a); ctx.fillRect(x, sT, bw, sB - sT); }
      ctx.fillStyle = emRgba(fill, Math.min(1, a + 0.3));      // dotted: ground only this minute has covered
      for (let y = t; y < Math.min(b, yCH); y += 3) ctx.fillRect(x, y, bw, 1.5);
      for (let y = Math.max(t, yCL); y < b; y += 3) ctx.fillRect(x, y, bw, 1.5);
    }
    return { yH, yL };
  };
  const drawn: Record<string, { yH: number; yL: number } | null> = {};
  for (const B of EM_BARS) { const r = R[B.tf]; drawn[B.tf] = r ? bar(r, colL + B.x, B.w, B.a, zonesOfBar(B.tf)) : null; }
  const b5 = drawn['5m'] ?? null;
  const yp = Y(s.price);
  if (yp != null) { const yy = Math.round(yp); ctx.fillStyle = 'rgba(10,11,13,0.92)'; ctx.fillRect(colL + 1, yy - 2, EM_W - 2, 4); ctx.fillStyle = '#eef0f3'; ctx.fillRect(colL + 1, yy - 1, EM_W - 2, 2); }
  ctx.restore();
  // an arrow where a bar runs off the pane (the day's high or low out of view)
  ctx.fillStyle = '#a3a9b3';
  for (const B of EM_BARS) {
    const d = drawn[B.tf], cx = colL + B.x + B.w / 2; if (!d) continue;
    if (d.yH < 0) { ctx.beginPath(); ctx.moveTo(cx, 1); ctx.lineTo(cx + 3, 6); ctx.lineTo(cx - 3, 6); ctx.closePath(); ctx.fill(); }
    if (d.yL > paneH - EM_FOOT) { const yb = paneH - EM_FOOT - 1; ctx.beginPath(); ctx.moveTo(cx, yb); ctx.lineTo(cx + 3, yb - 5); ctx.lineTo(cx - 3, yb - 5); ctx.closePath(); ctx.fill(); }
  }
  // the labels, one row at the foot of the column
  ctx.fillStyle = 'rgba(7,8,10,0.92)'; ctx.fillRect(colL, paneH - EM_FOOT, EM_W, EM_FOOT);
  ctx.font = EM_LAB; ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic'; ctx.fillStyle = '#a3a9b3';
  for (const B of EM_BARS) if (drawn[B.tf]) ctx.fillText(B.lab, colL + B.x + B.w / 2, paneH - 3);

  // labels over everything
  if (cchip) { ctx.globalAlpha = A; emChip(ctx, cchip.x, cchip.y, cchip.text, { font: EM_MONO, bg: 'rgba(10,11,13,0.85)', fg: c.down, h: 13 }); }
  if (chip) {
    ctx.globalAlpha = pv.stale ? 0.55 : 1; ctx.font = EM_TP_FONT;
    const cw = ctx.measureText(chip.text).width + 12, o = { font: EM_TP_FONT, bg: 'rgba(12,14,18,0.9)', bd: 'rgba(255,255,255,0.16)', fg: pv.stale ? '#a3a9b3' : '#eef0f3' };
    if (chip.x0 - 6 - cw >= 4) emChip(ctx, chip.x0 - 6, chip.y, chip.text, Object.assign({ right: true }, o));
    else emChip(ctx, chip.x0 + 4, chip.y + (m.side === 'short' ? -13 : 13), chip.text, o);
  }
  ctx.globalAlpha = 1;

  // hit areas + tip follow the axis
  if (hb) {
    if (!b5) hb.style.display = 'none';
    else {
      let top = Infinity, bot = -Infinity;
      for (const B of EM_BARS) { const d = drawn[B.tf]; if (d) { top = Math.min(top, d.yH); bot = Math.max(bot, d.yL); } }
      top = Math.max(0, top - 8); bot = Math.min(paneH, bot + 8);
      hb.style.display = ''; hb.style.transform = 'translateY(' + top + 'px)'; hb.style.height = Math.max(16, bot - top) + 'px';
    }
  }
  if (ht) { if (yp == null) ht.style.display = 'none'; else { ht.style.display = ''; ht.style.transform = 'translateY(' + (yp - 10) + 'px)'; } }
  if (tp && s.tip) { const ty = s.tip.kind === 'tag' ? yp : Y(s.tip.price); if (ty != null) tp.style.transform = 'translateY(' + ty + 'px) translateY(-50%)'; }
}

export function EntryMarksLayer({ chartApiRef, symbol, marks, price, colors, railW, record }: {
  chartApiRef: any; symbol: string; marks: EntryMarks | null; price: number | null; colors: any; railW: number; record: EntryRecord;
}) {
  const rootRef = useRefEM<any>(null), cvRef = useRefEM<any>(null), hbRef = useRefEM<any>(null), htRef = useRefEM<any>(null), tipRef = useRefEM<any>(null);
  const [tip, setTip] = useStateEM<any>(null);
  const rw = railW || 0;
  const rd = marks ? readMarks(marks, price) : null;
  const zcol = (z: string | undefined) => (z === 'good' ? colors.up : z === 'bad' ? colors.down : z === 'none' ? '#8b93a1' : colors.warn);
  const tagCol = rd ? zcol(rd.tag) : null;
  const stRef = useRefEM<any>({}); stRef.current = { symbol, marks, price, colors, rw, tip };

  // 3 · the last-price tag on the axis takes the colour
  useEffectEM(() => { const api = chartApiRef.current; if (api && api.setTag) api.setTag(tagCol); }, [tagCol]);
  // reserve the column: the S/R rail (and the order pills docked against it) step left by EM_W
  useEffectEM(() => {
    const host = rootRef.current && rootRef.current.parentElement;
    if (host) host.style.setProperty('--em-w', EM_W + 'px');
    return () => { if (host) host.style.removeProperty('--em-w'); const api = chartApiRef.current; if (api && api.setTag) api.setTag(null); };
  }, []);
  useEffectEM(() => {
    let raf = 0, last = '';
    const loop = () => {
      raf = requestAnimationFrame(loop);
      if (typeof document !== 'undefined' && document.hidden) return; // hidden tab: zero work
      const cv = cvRef.current, api = chartApiRef.current;
      if (!cv || !api || !api.chart) return;
      try {
        const k = emKey(cv, api, stRef.current);
        if (k === last) return;
        last = k;
        emDraw(cv, api, stRef.current, hbRef.current, htRef.current, tipRef.current);
      } catch (e) { last = ''; }
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);
  // a tapped tip stays until the next tap elsewhere
  useEffectEM(() => {
    if (!tip || !tip.pinned) return;
    const off = (e: any) => { if ((hbRef.current && hbRef.current.contains(e.target)) || (htRef.current && htRef.current.contains(e.target))) return; setTip(null); };
    document.addEventListener('pointerdown', off);
    return () => document.removeEventListener('pointerdown', off);
  }, [tip && tip.pinned]);

  function barAt(e: any) {
    const api = chartApiRef.current, root = rootRef.current, m = stRef.current.marks;
    if (!api || !root || !m || !m.ranges) return null;
    const rr = root.getBoundingClientRect(), x = e.clientX - (rr.right - EM_AXIS - EM_W), p = api.yToPrice(e.clientY - rr.top);
    const want = emBarAtX(x), tf = m.ranges[want] ? want : '5m', r = m.ranges[tf];
    if (p == null || !r) return null;
    const span = r.hi - r.lo, tol = Math.max(span * 0.06, 1e-6);
    if (p > r.hi + tol || p < r.lo - tol) return null;
    const pos = span > 0 ? (p - r.lo) / span : 0.5, third = pos >= 2 / 3 ? 0 : pos >= 1 / 3 ? 1 : 2;
    const ext = tf === '15m' && !!r.closed && (p > r.closed.hi || p < r.closed.lo);
    return { kind: 'bar', tf, third, ext, price: p, key: tf + ':' + third + ':' + ext };
  }
  const onBarsMove = (e: any) => { const b = barAt(e); setTip((t: any) => (t && t.pinned ? t : !b ? null : t && t.key === b.key ? t : b)); };
  const onBarsClick = (e: any) => { e.stopPropagation(); const b = barAt(e); setTip((t: any) => (!b || (t && t.pinned && t.key === b.key) ? null : Object.assign(b, { pinned: true }))); };
  const onLeave = () => setTip((t: any) => (t && t.pinned ? t : null));
  const onTagEnter = () => setTip((t: any) => (t && t.pinned ? t : { kind: 'tag', key: 'tag' }));
  const onTagClick = (e: any) => { e.stopPropagation(); setTip((t: any) => (t && t.pinned && t.key === 'tag' ? null : { kind: 'tag', key: 'tag', pinned: true })); };

  let tipEl = null;
  if (tip && marks && rd) {
    const zs = (marks.zones || ({} as any))[marks.side] || [];
    let dot = tagCol, text = rd.reason;
    if (tip.kind === 'bar') {
      const third = EM_THIRD[tip.third]!.toLowerCase(), rec: any = record || {};
      dot = zcol(zs[tip.third]);
      const yours = (r: any) => 'your ' + marks.side + 's here: ' + r[0] + ' · ' + emUsd(r[1]) + ' · ' + r[2] + '% won';
      if (tip.tf === '15m') text = tip.ext ? '15m · beyond the last 15 minutes — the dotted part is this minute'
        : '15m · ' + third + ' third · context only — not in the tag until your 15-min results are in' + (rec.since15m ? ' (recording since ' + rec.since15m + ')' : '');
      else if (tip.tf === 'D' || tip.tf === '4h') {
        const r = (marks.ranges as any)[tip.tf], cells = tip.tf === 'D' ? rec.day : rec.r240, cell = cells && (cells[marks.side] || [])[tip.third];
        const zones = (tip.tf === 'D' ? marks.zonesDay : marks.zones4h) || ({} as any);
        dot = zcol((zones[marks.side] || [])[tip.third]);
        const off = r && price != null ? (r.hi - price) / r.hi * 100 : null;
        // the hovered third and his record there first (as the 5m tip), then the range and where the price is in it
        text = (tip.tf === 'D' ? 'Day' : '4H') + ' · ' + third + ' third · '
          + (cell ? yours(cell) : tip.tf === '4h' ? 'no record yet' + (rec.since240 ? ' (recording since ' + rec.since240 + ')' : ' — the journal records it from Oct 23') : 'no record')
          + ' · ' + (tip.tf === 'D' ? 'today since 4:00 ' : 'the last 4 hours ') + emPx(r.lo) + '–' + emPx(r.hi)
          + (off == null ? '' : off < 1 ? ', price at the high' : ', price ' + Math.round(off) + '% off the high');
      }
      else { const r = (rec[marks.side] || [])[tip.third]; text = '5m · ' + third + ' third · ' + (r ? yours(r) : (zs[tip.third] || 'no record')); }
    }
    const api0 = chartApiRef.current, yv = api0 && api0.priceToY ? api0.priceToY(tip.kind === 'tag' ? price : tip.price) : null;
    tipEl = React.createElement('div', { className: 'em-tip', ref: tipRef, style: { right: (EM_AXIS + EM_W + rw + 8) + 'px', transform: 'translateY(' + (yv == null ? -999 : yv) + 'px) translateY(-50%)' } },
      React.createElement('i', { style: { background: dot } }),
      React.createElement('span', null, text));
  }

  return React.createElement('div', { className: 'em-layer', ref: rootRef },
    React.createElement('canvas', { className: 'em-canvas', ref: cvRef }),
    React.createElement('div', { className: 'em-hit bars', ref: hbRef, style: { display: 'none' }, onMouseMove: onBarsMove, onMouseLeave: onLeave, onClick: onBarsClick }),
    React.createElement('div', { className: 'em-hit tag', ref: htRef, style: { display: 'none' }, onMouseEnter: onTagEnter, onMouseLeave: onLeave, onClick: onTagClick }),
    tipEl);
}

